import moment from 'moment';
import { TabIdx } from 'src/containers/HomeContainers/Timesheet/tableHelpers';
import {
  convertResponseToDailyTimesheet,
  convertResponseToEmployeeInfo,
  getWeekDays,
  INITIAL_COMMENTS,
  INITIAL_DAILY_TIMESHEET,
} from 'src/containers/TimesheetContainers/helpers';
import { ErrorService } from 'src/services';
import { DateFormat, getInitialDateRange, getWeekEndDay, getWeekStartDay } from 'src/utils/momentUtils';
import { createReducer } from 'typesafe-actions';
import {
  addDailyTimesheetsAsync,
  addWeeklyTimesheetAsync,
  deleteDailyTimesheetsInWeekByIdAsync,
  exportCSVTimesheetsAsync,
  getDailyTimesheetsByWeeklyAsync,
  getTimesheetsAsync,
  getWeeklyTimesheetsByIdAsync,
  setActiveTimesheetTab,
  setDateRange,
  setSelectedDailyTimesheet,
  setSelectedTimesheet,
  setTimesheetModalType,
  setTimesheets,
} from './actions';
import { DailyTimesheet, EmployeeInfo, Timesheet, TimesheetModalType, WeeklyTimesheet } from './types';

/* ------------- Types and Action Creators ------------- */

/* ------------- Initial State ------------- */

export const InitialEmployeeInfo: EmployeeInfo = {
  id: '',
  employeeId: '',
  name: '',
  rate: '',
  level: '',
  trade: '',
};

export const InitialWeeklyTableData: WeeklyTimesheet = {
  startDate: getWeekStartDay().format(DateFormat),
  endDate: getWeekEndDay().format(DateFormat),
  employeeInfo: InitialEmployeeInfo,
  dailyTimes: getWeekDays(),
};

export type ITimesheetState = Readonly<{
  timesheets: Array<Timesheet>;
  totalTimesheet: number;
  isLoading: boolean;
  weeklyTableData: WeeklyTimesheet;
  dailyTableData: DailyTimesheet;
  commentOptions: any[];
  openModalType: TimesheetModalType;
  activeTimesheetTab: number;
  dateRange: [Date, Date];
  // queryParams: GetTimesheetsParams | null;
}>;

const initialState: ITimesheetState = {
  timesheets: [],
  totalTimesheet: 0,
  isLoading: false,
  weeklyTableData: InitialWeeklyTableData,
  dailyTableData: INITIAL_DAILY_TIMESHEET,
  commentOptions: INITIAL_COMMENTS,
  openModalType: null,
  activeTimesheetTab: TabIdx.Week,
  dateRange: getInitialDateRange(false),
  // queryParams: null,
};

/* ------------- Selectors ------------- */

export default createReducer(initialState)
  /* ------------- signInAsync ------------- */
  .handleAction(getTimesheetsAsync.request, state => ({
    ...state,
    isLoading: true,
  }))
  .handleAction(getTimesheetsAsync.success, (state, action) => ({
    ...state,
    isLoading: false,
    timesheets: action.payload.data as Timesheet[],
    totalTimesheet: action.payload.total,
    error: null,
  }))
  .handleAction(getTimesheetsAsync.failure, (state, action) => ({
    ...state,
    isLoading: false,
    error: ErrorService.asyncFailure(action.payload.data),
  }))

  .handleAction(getDailyTimesheetsByWeeklyAsync.request, state => ({
    isLoading: true,
    ...state,
  }))
  .handleAction(getDailyTimesheetsByWeeklyAsync.success, (state, action) => ({
    ...state,
    isLoading: false,
    weeklyTableData: {
      ...state.weeklyTableData,
      dailyTimes: convertResponseToDailyTimesheet(state.weeklyTableData, action.payload.dailyTimesheets),
    },
    error: null,
  }))
  .handleAction(getDailyTimesheetsByWeeklyAsync.failure, (state, action) => ({
    ...state,
    isLoading: false,
    error: ErrorService.asyncFailure(action.payload.data),
  }))

  .handleAction(getWeeklyTimesheetsByIdAsync.request, state => ({
    ...state,
    isLoading: true,
  }))
  .handleAction(getWeeklyTimesheetsByIdAsync.success, (state, action) => ({
    ...state,
    isLoading: false,
    weeklyTableData: {
      ...state.weeklyTableData,
      id: action.payload.id,
      status: action.payload.status,
      startDate: moment(action.payload.startDate).format(DateFormat),
      endDate: moment(action.payload.endDate).format(DateFormat),
      employeeInfo: convertResponseToEmployeeInfo(action.payload),
    },
    error: null,
  }))
  .handleAction(getWeeklyTimesheetsByIdAsync.failure, (state, action) => ({
    ...state,
    isLoading: false,
    error: ErrorService.asyncFailure(action.payload.data),
  }))
  /* ------------- exportCSVTimesheet ------------- */
  .handleAction(exportCSVTimesheetsAsync.request, state => ({
    ...state,
    isLoading: true,
  }))
  .handleAction(exportCSVTimesheetsAsync.success, (state, action) => ({
    ...state,
    isLoading: false,
    error: null,
  }))
  .handleAction(exportCSVTimesheetsAsync.failure, (state, action) => ({
    ...state,
    isLoading: false,
    error: ErrorService.asyncFailure(action.payload.data),
  }))

  /* ------------- timesheetAddAsync ------------- */

  .handleAction(addWeeklyTimesheetAsync.request, state => ({
    ...state,
    isLoading: true,
  }))
  .handleAction(addWeeklyTimesheetAsync.success, (state, action) => ({
    ...state,
    isLoading: false,
    openModalType: false,
    error: null,
    weeklyTableData: {
      ...state.weeklyTableData,
      id: action.payload.id,
      startDate: moment(action.payload.startDate).format(DateFormat),
      endDate: moment(action.payload.endDate).format(DateFormat),
    },
  }))
  .handleAction(addWeeklyTimesheetAsync.failure, (state, action) => ({
    ...state,
    isLoading: false,
    error: ErrorService.asyncFailure(action.payload.data),
  }))

  /* ------------- dailyTimesheetsAddAsync ------------- */
  .handleAction(addDailyTimesheetsAsync.request, state => ({
    ...state,
    isLoading: true,
  }))
  .handleAction(addDailyTimesheetsAsync.success, (state, action) => ({
    ...state,
    isLoading: false,
    error: null,
    openModalType: false,
    weeklyTableData: {
      ...state.weeklyTableData,
      dailyTimes: convertResponseToDailyTimesheet(state.weeklyTableData, action.payload.dailyTimesheets),
    },
  }))
  .handleAction(addDailyTimesheetsAsync.failure, (state, action) => ({
    ...state,
    isLoading: false,
    error: ErrorService.asyncFailure(action.payload.data),
  }))

  /* ------------- dailyTimesheetsDeleteAsync ------------- */
  .handleAction(deleteDailyTimesheetsInWeekByIdAsync.request, state => ({
    ...state,
    isLoading: true,
  }))
  .handleAction(deleteDailyTimesheetsInWeekByIdAsync.success, (state, action) => {
    const deletedDailyTime = {
      jobs: [{ hours: null, isExtra: false, job: null, lots: '', phaseCode: null, task: '' }],
      startTime: null,
      endTime: null,
      id: undefined,
      comments: [],
      isNotWorking: false,
    };

    const dailyTimes = state.weeklyTableData.dailyTimes.map(dailyTime => {
      return dailyTime.id
        ? dailyTime.id !== action.payload.dailyTimesheetId
          ? dailyTime
          : {
              ...dailyTime,
              ...deletedDailyTime,
            }
        : dailyTime;
    });
    return {
      ...state,
      isLoading: false,
      error: null,
      openModalType: false,
      weeklyTableData: {
        ...state.weeklyTableData,
        dailyTimes,
      },
    };
  })
  .handleAction(deleteDailyTimesheetsInWeekByIdAsync.failure, (state, action) => ({
    ...state,
    isLoading: false,
    error: ErrorService.asyncFailure(action.payload.data),
  }))

  /* ------------- setTimesheets ------------- */
  .handleAction(setTimesheets, (state, action) => ({
    ...state,
    timesheets: action.payload,
  }))
  .handleAction(setTimesheetModalType, (state, action) => ({
    ...state,
    openModalType: action.payload,
  }))
  .handleAction(setSelectedDailyTimesheet, (state, action) => ({
    ...state,
    dailyTableData: action.payload,
  }))
  .handleAction(setSelectedTimesheet, (state, action) => ({
    ...state,
    weeklyTableData: action.payload,
  }))
  .handleAction(setActiveTimesheetTab, (state, action) => ({
    ...state,
    activeTimesheetTab: action.payload,
    queryParams: { ...state.queryParams, skip: 0, take: 10 },
  }))
  .handleAction(setDateRange, (state, action) => ({
    ...state,
    dateRange: action.payload,
    queryParams: { ...state.queryParams, skip: 0, take: 10 },
  }));
